import { Component } from '@theme/component';
import { clamp } from '@theme/utilities';
import { cycleFocus } from '@theme/focus';

/**
 * Two-pane technique showcase: media (with the tabs on top) | heading + content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} container
 * @property {HTMLElement[]} [links]
 * @property {HTMLElement[]} [backgrounds]
 * @property {HTMLElement[]} [panels]
 *
 * @extends {Component<Refs>}
 */
class CollectionLinksBackground extends Component {
  requiredRefs = ['container'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#handleKeydown);
    this.#syncVideos(this.currentIndex);
  }

  get links() {
    return this.refs.links || [];
  }

  get currentIndex() {
    return this.links.findIndex((link) => link.getAttribute('aria-current') === 'true');
  }

  /**
   * Activate a tab by index — updates labels, media, side panel, and video playback.
   *
   * @param {number | string} targetIndex
   * @param {Event} [event]
   */
  select(targetIndex, event) {
    if (event instanceof PointerEvent && event.type === 'click') {
      event.preventDefault();
    }

    const { links, backgrounds, panels } = this.refs;
    const index = clamp(Number(targetIndex), 0, Math.max((links?.length || 1) - 1, 0));

    if (links) {
      for (const [i, link] of links.entries()) {
        const active = i === index;
        link.setAttribute('aria-current', String(active));
        link.setAttribute('aria-selected', String(active));
        link.setAttribute('tabindex', active ? '0' : '-1');
      }
    }

    if (backgrounds) {
      for (const [i, background] of backgrounds.entries()) {
        background.toggleAttribute('data-active', i === index);
      }
    }

    if (panels) {
      for (const [i, panel] of panels.entries()) {
        const active = i === index;
        panel.toggleAttribute('data-active', active);
        panel.toggleAttribute('hidden', !active);
      }
    }

    this.#syncVideos(index);
    this.#scrollTabIntoView(links?.[index]);
  }

  /**
   * Keep the active tab visible when the tabs row scrolls (mobile).
   *
   * @param {HTMLElement | undefined} tab
   */
  #scrollTabIntoView(tab) {
    const { container } = this.refs;
    if (!tab || container.scrollWidth <= container.clientWidth) return;

    container.scrollTo({
      left: tab.offsetLeft - (container.clientWidth - tab.offsetWidth) / 2,
      behavior: 'smooth',
    });
  }

  /**
   * Play the active video and pause/reset the rest.
   *
   * @param {number} activeIndex
   */
  #syncVideos(activeIndex) {
    const { backgrounds } = this.refs;
    if (!backgrounds) return;

    for (const [i, background] of backgrounds.entries()) {
      const video = background.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) continue;

      if (i === activeIndex) {
        video.play().catch(() => {});
      } else {
        video.pause();
        video.currentTime = 0;
      }
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    let modifier = 0;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        modifier = 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        modifier = -1;
        break;
      default:
        return;
    }

    event.preventDefault();
    cycleFocus(this.links, modifier);

    const focused = this.links.find((link) => link === document.activeElement);
    if (focused) {
      const index = this.links.indexOf(focused);
      if (index >= 0) this.select(index);
    }
  };
}

if (!customElements.get('collection-links-background-component')) {
  customElements.define('collection-links-background-component', CollectionLinksBackground);
}
